import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import useReveal from "@/hooks/useReveal";

const CHAPTER = [
  { n: "01", t: "SPARK", d: "A real situation that reveals what the student cannot yet handle." },
  { n: "02", t: "Can You Handle These?", d: "An honest self-check that turns 'no' into motivation." },
  { n: "03", t: "Did You Know?", d: "Surprising facts that create curiosity." },
  { n: "04", t: "The Story", d: "A named, real-world story that anchors the lesson emotionally." },
  { n: "05", t: "Core Concepts", d: "The five key ideas, explained simply." },
  { n: "06", t: "Teacher's Corner", d: "A facilitation guide that makes any teacher an expert." },
  { n: "07", t: "Situations", d: "10 to 15 real scenarios role-played, with the exact words said aloud." },
  { n: "08", t: "Workbook & Reflect", d: "Home tasks that carry the learning back to the family." },
];

const SITUATION = [
  "What is happening",
  "How it feels",
  "What most people do wrong",
  "What you should do",
  "The exact words to say",
  "What happens next",
];

export default function TheProgram() {
  useReveal();
  useEffect(() => { document.title = "The Program — LifeIQ"; }, []);

  return (
    <main>
      {/* HERO */}
      <section
        className="bg-[#0D1B2A] text-white relative"
        style={{
          backgroundImage:
            "linear-gradient(rgba(13,27,42,0.95) 0%, rgba(13,27,42,0.97) 100%), url('https://images.unsplash.com/photo-1481627834876-b7833e8f5570?auto=format&fit=crop&w=1920&q=60')",
          backgroundSize: "cover",
          backgroundPosition: "center right",
        }}
      >
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 pt-20 md:pt-28 pb-24 md:pb-28">
          <div className="max-w-4xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">The Program</div>
            <h1 className="mt-5 font-serif-d text-[42px] sm:text-6xl lg:text-[68px] leading-[1.04] tracking-tight">
              A complete life-skills education, built like a curriculum — not a workshop.
            </h1>
            <p className="mt-8 text-[17px] sm:text-[19px] text-white/85 leading-[1.7] max-w-3xl">
              Three levels, 72 chapters, over a thousand real-life situations, a dedicated cyber-safety module, and full teacher training — structured to move learning from the page into real-life reflex.
            </p>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="bg-white">
        <div className="max-w-[920px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#004D40] font-semibold">Philosophy</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              Knowing is not enough. We build reflexes.
            </h2>
          </div>
          <p className="mt-10 text-[17px] sm:text-[19px] text-[#1A1A1A] leading-[1.8] reveal">
            A student can read every word about fraud, score full marks on a test, and still scan a scammer's QR code the next week — because knowledge in memory does not become action under pressure. Only practice does. Every LifeIQ chapter is built to move learning from the page into automatic response, the way a fire drill works better than a fire lecture.
          </p>

          <figure className="mt-14 reveal">
            <blockquote className="font-serif-d italic text-[24px] sm:text-[30px] leading-[1.3] text-[#F57F17] border-l-[3px] border-[#F57F17] pl-7">
              "LifeIQ exists to reduce the gap between the complexity of the real world and a student's ability to navigate it confidently."
            </blockquote>
          </figure>
        </div>
      </section>

      {/* 8-PART CHAPTER STRUCTURE */}
      <section className="bg-[#F5F5F4]">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#004D40] font-semibold">Chapter design</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              How every chapter works.
            </h2>
            <p className="mt-6 text-[17px] sm:text-[19px] text-[#5C6470] leading-[1.7]">
              All 72 chapters follow the same eight-part structure — designed to create the gap, then close it through practice.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {CHAPTER.map((c, i) => (
              <div
                key={c.n}
                data-testid={`chapter-step-${i}`}
                className="reveal bg-white border border-[#E5E7EA] rounded-sm p-7 flex items-start gap-6"
                style={{ transitionDelay: `${(i % 2) * 80}ms` }}
              >
                <div className="font-serif-d text-[36px] text-[#F57F17] leading-none shrink-0">{c.n}</div>
                <div>
                  <div className="font-serif-d text-[20px] text-[#0D1B2A]">{c.t}</div>
                  <p className="mt-2 text-[15px] text-[#5C6470] leading-[1.65]">{c.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SITUATION METHOD */}
      <section className="bg-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#004D40] font-semibold">The situation method</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              Every situation is practised, not just read.
            </h2>
            <p className="mt-6 text-[17px] sm:text-[19px] text-[#5C6470] leading-[1.7]">
              Each of the 1,080+ situations follows the same six-part template — the engine that turns knowledge into reflex:
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 reveal">
            {SITUATION.map((s, i) => (
              <div key={s} className="border-l-[3px] border-[#004D40] pl-5 py-2">
                <div className="text-[12px] uppercase tracking-[0.18em] text-[#004D40] font-semibold">Step {String(i + 1).padStart(2, "0")}</div>
                <div className="mt-2 font-serif-d text-[20px] text-[#0D1B2A]">{s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EVIDENCE-BASED */}
      <section className="bg-[#F5F5F4]">
        <div className="max-w-[920px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#004D40] font-semibold">Evidence-based</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              Grounded in evidence — not inspiration.
            </h2>
          </div>
          <p className="mt-10 text-[17px] sm:text-[19px] text-[#1A1A1A] leading-[1.8] reveal">
            Every claim is research-backed: neuroscience for emotional control, SEBI data for investing, actuarial maths for insurance, and real documented fraud cases for cyber safety. LifeIQ does not motivate students with slogans. It equips them with what is true.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0D1B2A] text-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-28 text-center">
          <div className="reveal">
            <h2 className="font-serif-d text-[36px] sm:text-5xl lg:text-[58px] leading-[1.06] text-white max-w-3xl mx-auto">
              See the three levels in detail.
            </h2>
            <Link to="/#levels" data-testid="program-cta" className="mt-10 inline-flex items-center gap-2 bg-[#F57F17] text-white px-8 py-4 text-[13px] font-semibold tracking-wide uppercase hover:brightness-95 transition rounded-sm">
              Explore the Three Levels <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
